import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Ban, Flag, MoreHorizontal, X } from 'lucide-react';
import { REPORT_REASONS, SAFETY_COPY } from '@saesak/shared';
import { Button, Chip, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { useBlockMutations, useBlocks, type ReportTarget } from '@/lib/safety';
import { useAuth } from '@/store/auth';

/**
 * 안전 도구 — 명세서 §5.30 (v1.21): 신고 · 차단.
 * 쪽지·댓글·사진·알림장·가정 소식 옆의 ⋯ 메뉴가 이 컴포넌트다.
 * 신고는 원장에게 가고, 차단은 내 화면에서 상대의 쪽지·댓글을 감춘다.
 */

/** 신고 시트 — 사유를 고르고 보낸다. 접수되면 안내만 남기고 닫는다 */
export function ReportSheet({ target, onClose }: { target: ReportTarget; onClose: () => void }) {
  const [reason, setReason] = useState<string>('');
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      apiData('/safety/reports', {
        method: 'POST',
        body: { targetType: target.type, targetId: target.id, reason, detail: detail.trim() || undefined },
      }),
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '신고를 보내지 못했어요.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50" role="dialog" aria-modal="true">
      <div className="w-full max-w-screen-sm rounded-t-md bg-surface p-4 pb-[calc(var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+16px)]">
        <div className="mb-3 flex items-center">
          <p className="flex-1 text-lg font-bold">{SAFETY_COPY.reportTitle}</p>
          <button
            type="button"
            aria-label="닫기"
            className="tap grid place-items-center rounded-full text-ink-sub hover:bg-primary-soft"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {submit.isSuccess ? (
          <>
            <p className="rounded-sm bg-primary-soft px-3 py-3 text-base text-primary-strong">{SAFETY_COPY.reportDone}</p>
            <Button block className="mt-3" onClick={onClose}>
              닫기
            </Button>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <div role="group" aria-label={SAFETY_COPY.reportReasonLabel}>
              <p className="mb-1.5 text-sm font-medium">{SAFETY_COPY.reportReasonLabel}</p>
              <div className="flex flex-wrap gap-1.5">
                {REPORT_REASONS.map((item) => (
                  <Chip
                    key={item.code}
                    tone={reason === item.code ? 'primary' : 'neutral'}
                    selected={reason === item.code}
                    onClick={() => setReason(item.code)}
                  >
                    {item.label}
                  </Chip>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">{SAFETY_COPY.reportDetailLabel}</span>
              <Textarea
                rows={3}
                value={detail}
                maxLength={500}
                placeholder={SAFETY_COPY.reportDetailPlaceholder}
                onChange={(e) => setDetail(e.target.value)}
              />
            </label>
            <p className="text-sm text-ink-sub">{SAFETY_COPY.reportHint}</p>
            {error && (
              <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
                {error}
              </p>
            )}
            <Button block disabled={!reason} loading={submit.isPending} onClick={() => submit.mutate()}>
              {SAFETY_COPY.reportSubmit}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ⋯ 메뉴 — 신고, 그리고 상대가 있으면 차단/해제.
 * 내가 쓴 내용에는 나타나지 않는다.
 */
export function SafetyMenu({
  target,
  user,
  className,
}: {
  target: ReportTarget;
  /** 콘텐츠를 쓴 사람 — 있으면 차단 항목이 붙는다 */
  user?: { id: string; name: string } | null;
  className?: string;
}) {
  const me = useAuth((s) => s.user?.id);
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const blocks = useBlocks();
  const { block, unblock } = useBlockMutations();

  if (user && user.id === me) return null;
  const isBlocked = Boolean(user && blocks.data?.some((row) => row.userId === user.id));

  return (
    <span className={`relative inline-flex ${className ?? ''}`}>
      <button
        type="button"
        aria-label={SAFETY_COPY.menu}
        aria-haspopup="menu"
        aria-expanded={open}
        className="tap grid h-9 w-9 place-items-center rounded-full text-ink-sub hover:bg-primary-soft"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="메뉴 닫기"
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div role="menu" className="absolute right-0 top-9 z-40 min-w-36 rounded-md border border-line bg-surface p-1 shadow-card">
            <MenuItem
              icon={<Flag size={14} />}
              label={SAFETY_COPY.report}
              onClick={() => {
                setOpen(false);
                setReporting(true);
              }}
            />
            {user && (
              <MenuItem
                icon={<Ban size={14} />}
                label={isBlocked ? SAFETY_COPY.unblock : SAFETY_COPY.block}
                onClick={() => {
                  setOpen(false);
                  if (isBlocked) unblock.mutate(user.id);
                  else if (window.confirm(SAFETY_COPY.blockConfirm(user.name))) block.mutate(user.id);
                }}
              />
            )}
          </div>
        </>
      )}

      {reporting && <ReportSheet target={target} onClose={() => setReporting(false)} />}
    </span>
  );
}

function MenuItem({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      className="tap flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-base hover:bg-primary-soft"
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}
